import { toast } from '@/components/toast';
import { audiosDns, maxAudioSizeInBytes } from '@/environment';
import { HttpStatus } from '@/errors/http-status';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useAnalytics } from '@/services/analytics';
import { uploadAudio } from '@/services/api';
import { logger } from '@/services/logger';
import { ERROR_KINDS } from '@/state/layout/moderation';
import { formatS3Url } from '@/utils/s3';
import { renameFile } from '@/utils/utils';
import { AxiosError } from 'axios';
import { nanoid } from 'nanoid';
import { useMutation } from 'react-query';
import { atom, useResetRecoilState, useSetRecoilState } from 'recoil';

export const uploadedCloneVoice = atom<AudioData | null>({
    key: 'clone-voice-uploaded-file',
    default: null,
});

const MAX_DURATION = 5 * 60;

export function useUploadClonedAudio() {
    const setUploadedAudio = useSetRecoilState(uploadedCloneVoice);
    const mixpanel = useAnalytics();

    return useMutation(uploadAudio, {
        retry(retryCount, error) {
            const errorUnhandled = !error?.response?.data?.kind;
            return errorUnhandled && retryCount < 1;
        },
        onSuccess({ url, duration }) {
            const urlData = url.split('/') || [];
            const name = urlData[urlData.length - 1];
            const listenUrl = formatS3Url(audiosDns, url);
            setUploadedAudio({ data: url, duration, listenUrl, name });
            mixpanel?.track('upload-clone-voice-audio', { event: 'success', duration: duration });
        },
        onError: (
            error: AxiosError<{ kind: string; description?: string; details?: { terms: string[]; metadata: Record<string, any> } }>
        ) => {
            const kind = error?.response?.data?.kind;
            mixpanel?.track('upload-audio', { kind, event: 'error' });

            if (error.code === 'ERR_CANCELED') {
                return;
            } else if (kind === ERROR_KINDS.UnsupportedMimeTypeError) {
                toast.info({ title: 'The file uploaded is not supported' });
                logger.warn('Unsupported MIME type audio uploaded', { error });
            } else if (kind === ERROR_KINDS.ValidationError) {
                toast.error({ title: 'The file uploaded did not pass our validation' });
                logger.warn('Audio validation failed', { error });
            } else if (error.response?.status === HttpStatus.PAYLOAD_TOO_LARGE) {
                toast.info({ title: 'Audio file should be up to 5 minutes', toastId: `upload-audio-error` });
            } else {
                toast.error({ title: 'Failed to upload audio' });
                logger.error('Failed to upload audio', { error });
            }
        },
    });
}

export default function () {
    const resetUploadedAudio = useResetRecoilState(uploadedCloneVoice);

    const { mutateAsync: uploadAudio } = useUploadClonedAudio();

    return {
        async reset() {
            resetUploadedAudio();
        },
        upload: async (file?: File, blob?: Blob): Promise<[string, number, string]> => {
            if (file || blob) {
                if (file && !file.type.match(/^audio\/.*$|^video\/.*$/)) {
                    toast.error({ title: `Invalid audio type: ${file.type}` });
                } else if (file && file.size > maxAudioSizeInBytes) {
                    toast.error({ title: `Audio file must be less than ${Math.floor(maxAudioSizeInBytes * 0.00001) / 10}MB` });
                } else {
                    if (file) {
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(file);

                        await new Promise<void>(resolve => {
                            audio.addEventListener('loadedmetadata', () => {
                                resolve();
                            });
                        });

                        if (audio.duration > MAX_DURATION) {
                            toast.info({ title: 'Audio file should be up to 5 minutes', toastId: `upload-audio-error` });
                            return ['', -1, ''];
                        }
                    }
                    if (!file) {
                        file = new File([blob!], 'voice.webm', {
                            type: 'audio/webm;codecs=opus',
                        });
                    }

                    const { duration, sourceFile, url } = await uploadAudio({ audio: renameFile(file, `audio_${nanoid()}`) });
                    return [sourceFile, duration, url];
                }
            }
            return ['', -1, ''];
        },
    };
}
